import { useState } from 'react';

import { Field, FormGrid, FormWrapper, Title } from '../styled';

type Endereco = {
  cep: string;
  rua: string;
  numero: string;
  bairro: string;
  cidade: string;
  estado: string;
};

type StepEnderecoProps = {
  value: Endereco;
  onChange: (value: Endereco) => void;
};

export default function StepEndereco({ value, onChange }: StepEnderecoProps) {
  const set = (k: keyof Endereco, v: string) => onChange({ ...value, [k]: v });

  const [loadingCEP, setLoadingCEP] = useState(false);
  const [cepError, setCepError] = useState<string | null>(null);

  const formatCEP = (v: string) => {
    return v
      .replace(/\D/g, '')
      .slice(0, 8)
      .replace(/^(\d{5})(\d{0,3})$/, '$1-$2');
  };

  async function handleCepBlur() {
    const digits = value.cep.replace(/\D/g, '');

    setCepError(null); // limpa erro antigo

    if (digits.length !== 8) return;

    try {
      setLoadingCEP(true);
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();

      if (data.erro) {
        setCepError('CEP não encontrado.');
        return;
      }

      onChange({
        ...value,
        rua: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        estado: data.uf || '',
      });
    } catch (e) {
      setCepError('Erro ao buscar endereço. Verifique o CEP.');
    } finally {
      setLoadingCEP(false);
    }
  }

  return (
    <FormWrapper>
      <Title>Dados do endereço</Title>

      <FormGrid>
        <Field>
          <label>CEP</label>
          <input
            value={value.cep}
            onChange={(e) => {
              setCepError(null); // limpa mensagem ao digitar
              set('cep', formatCEP(e.target.value));
            }}
            onBlur={handleCepBlur}
            placeholder='00000-000'
            inputMode='numeric'
            autoComplete='postal-code'
          />

          {cepError && (
            <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
              {cepError}
            </div>
          )}
        </Field>

        <Field style={{ gridColumn: '1 / span 2' }}>
          <label>Rua {loadingCEP ? '(buscando...)' : ''}</label>
          <input
            value={value.rua}
            onChange={(e) => set('rua', e.target.value)}
            placeholder='Informe o nome da rua'
            autoComplete='address-line1'
          />
        </Field>

        <Field>
          <label>Número</label>
          <input
            value={value.numero}
            onChange={(e) => set('numero', e.target.value)}
            placeholder='Número da residência'
            inputMode='numeric'
            autoComplete='address-line2'
          />
        </Field>

        <Field>
          <label>Bairro</label>
          <input
            value={value.bairro}
            onChange={(e) => set('bairro', e.target.value)}
            placeholder='Digite o bairro'
            autoComplete='address-level3'
          />
        </Field>

        <Field>
          <label>Cidade</label>
          <input
            value={value.cidade}
            onChange={(e) => set('cidade', e.target.value)}
            placeholder='Digite a cidade'
            autoComplete='address-level2'
          />
        </Field>

        <Field>
          <label>Estado</label>
          <input
            value={value.estado}
            onChange={(e) =>
              set('estado', e.target.value.toUpperCase().slice(0, 2))
            }
            placeholder='UF (ex: SP)'
            autoComplete='address-level1'
            maxLength={2}
          />
        </Field>
      </FormGrid>
    </FormWrapper>
  );
}
